import { useState } from 'react';
import { Button, Card, Icon, Input, useToast } from '@efira/ui';

import { useGifs, useShowGif, useStream } from '../../api/queries';
import type { GifItemRow } from '../../api/queries';

/**
 * Гифки: поиск через наш маршрут, показ - в ОВЕРЛЕЕ.
 *
 * НАЖАТИЕ НА ПЛИТКУ ВЫВОДИТ ГИФКУ В КАДР, как пад выводит звук: не в
 * браузер стримера. Длительность показа - одно число, и оно объявлено здесь
 * же, а не спрятано константой в коде.
 *
 * ПУСТОЕ СОСТОЯНИЕ ДО ПЕРВОГО ПОИСКА - НЕ «НИЧЕГО НЕ НАЙДЕНО». Человек ещё
 * ничего не искал; сообщать ему об отсутствии результатов - врать о том,
 * чего не было.
 */
export function Gifs() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  /* Накопленные страницы: «Показать ещё» добавляет к показанному, а не
     заменяет его. */
  const [kept, setKept] = useState<GifItemRow[]>([]);

  const stream = useStream();
  const gifs = useGifs(submitted, page, submitted !== '' || page > 1);
  const show = useShowGif();
  const toast = useToast();

  const seconds = stream.data?.seconds ?? 10;
  const failure = gifs.error as { message?: string } | null;

  return (
    <div data-right="gifs" className="flex h-full min-h-0 flex-col">
      {/*
        Поиск ВО ВСЮ ШИРИНУ и без отдельной кнопки - как у эталона. Ищем по
        Enter, а не по каждой букве: каждая буква - это запрос к чужому
        провайдеру и расход общей квоты.
      */}
      <form
        data-control-row="gif-head"
        className="@container border-foreground/10 flex items-center gap-2 border-b px-3 py-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(query.trim());
          setPage(1);
          setKept([]);
        }}
      >
        <Icon name="search" size="sm" tone="muted" className="shrink-0" />
        <Input
          data-gif="search"
          aria-label="Найти гифку"
          placeholder="Найти гифку и нажать Enter"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className="min-w-0 flex-1"
        />
        {/* Кнопка остаётся доступной клавиатуре и чтецу, но не занимает
            место: у эталона её нет вовсе, а submit нужен форме.

            `sr-only` НА ОБЁРТКЕ, а не на кнопке: у кнопки пресета своя
            высота, и она перебивает высоту `sr-only` - кнопка оставалась
            в 32 px, лежала поверх поля и ворота строк это поймали
            («„“ и „Найти“ пересеклись»). */}
        <span className="sr-only">
          <Button variant="ghost" className="size-full" type="submit" data-gif="search-submit">
            Найти
          </Button>
        </span>
      </form>

      <div
        data-console-scroll="gifs"
        tabIndex={0}
        role="region"
        aria-label="Гифки"
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
      >
        {/* Отказ провайдера - ПРИЧИНОЙ, а не пустой сеткой. */}
        {failure !== null && (
          <p data-gif="failure" className="text-warning text-sm">
            {failure.message ?? 'Гифки сейчас недоступны. Попробуйте позже.'}
          </p>
        )}

        {failure === null && submitted === '' && page === 1 && (
          <p data-gif="empty" className="text-foreground/80 text-sm">
            Наберите слово — и гифки появятся здесь. Нажатие на любую выводит её на оверлей на{' '}
            {seconds} секунд; сколько именно — меняется в настройках эфира.
          </p>
        )}

        {gifs.data !== undefined && gifs.data.items.length === 0 && submitted !== '' && (
          <p data-gif="none" className="text-foreground/80 text-sm">
            По запросу «{submitted}» ничего нет. Попробуйте другое слово — или по-английски.
          </p>
        )}

        <div data-gif="grid" className="grid grid-cols-2 gap-2">
          {[...kept, ...(gifs.data?.items ?? [])].map((item, index) => (
            /*
              ПЛИТКА ГИФКИ - РОЛЬ «ПЛИТКА» (`Card`), а не кнопка: внутри
              изображение, а у кнопки пресета высота фиксирована
              (единица 17, ворота UI-7).
            */
            <Card asChild size="sm" key={`${item.id}-${index}`} className="relative gap-0 p-0">
              {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                  требует голого элемента; роль остаётся за Card. */}
              <button
                type="button"
                className="overflow-hidden"
                data-gif-tile={item.id}
                title={`${item.title} — на оверлей на ${seconds} с`}
                onClick={() => {
                  show.mutate(
                    { url: item.url },
                    {
                      onSuccess: () => {
                        toast.show({ tone: 'success', title: `Гифка на оверлее — ${seconds} с` });
                      },
                    },
                  );
                }}
              >
                <img src={item.preview} alt={item.title} loading="lazy" className="w-full" />

                {/* Пометка ставится ТОЛЬКО если провайдер отдал признак. */}
                {item.sponsored === true && (
                  <span
                    data-gif-sponsored={item.id}
                    className="bg-background/90 text-foreground absolute start-1 top-1 rounded px-1 text-xs"
                  >
                    реклама
                  </span>
                )}
              </button>
            </Card>
          ))}
        </div>

        {gifs.data?.more === true && (
          <Button
            variant="secondary"
            size="s"
            className="mt-2 w-full"
            data-gif="loadmore"
            onClick={() => {
              setKept((current) => [...current, ...(gifs.data?.items ?? [])]);
              setPage((current) => current + 1);
            }}
          >
            Показать ещё
          </Button>
        )}
      </div>
    </div>
  );
}
